import React, { useState, useRef, useEffect, useContext } from 'react'
import camera from "../../assets/img/Course/camera.svg"
import uploadCta from "../../assets/img/Course/upload-cta-icon.svg"
import draftIcon from "../../assets/img/Course/draft-icon.svg"
import checkIocn from "../../assets/img/Course/check-icon.svg"
import replaceThumbnail from "../../assets/img/Course/replace-thumbnail.svg"
import LessonContent, { getVideoEmbedUrl } from './LessonContent'
import AuthContext from '../../store/AuthContext'
import { Snackbar, Alert } from '@mui/material';
import useCourseThumbnailCrop from './useCourseThumbnailCrop'

const STATUS_OPTIONS = ['Draft', 'Published'];

const CreateLesson = ({ module, lesson, onBack, assessmentId, courseId, onLessonSaved }) => {
    const isEditMode = !!lesson;
    const moduleId = module?.id;
    const storageKey = `lesson_draft_${assessmentId}_${courseId}_${moduleId}_${lesson?.id || 'new'}`;

    const getDraft = () => {
        if (isEditMode) return null;
        try {
            const draftStr = sessionStorage.getItem(storageKey);
            if (draftStr) return JSON.parse(draftStr);
        } catch (e) { }
        return null;
    };
    const draft = getDraft();

    const [title, setTitle] = useState(draft?.title ?? (lesson?.title || ''));
    const [thumbnailFile, setThumbnailFile] = useState(lesson?.thumbnailFile || null);
    const [thumbnailPreview, setThumbnailPreview] = useState(() => {
        if (draft?.thumbnailPreview && !draft.thumbnailPreview.startsWith('blob:')) return draft.thumbnailPreview;
        return lesson?.thumbnail_url || null;
    });
    const [isloading, setIsLoading] = useState(false);
    const [createdLessonId, setCreatedLessonId] = useState(null);
    const activeLessonId = lesson?.id || createdLessonId;
    const isEditModeLocal = !!activeLessonId;
    const [toasts, setToasts] = useState([]);

    const setToast = (toastObj) => {
        if (!toastObj.open) return;
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { ...toastObj, id }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, toastObj.severity === 'success' ? 3000 : 4000);
    };

    const handleCloseToast = (id) => (event, reason) => {
        if (reason === 'clickaway') return;
        setToasts(prev => prev.filter(t => t.id !== id));
    };
    const [blocks, setBlocks] = useState(() => {
        if (draft?.blocks) {
            return draft.blocks.map(b => ({
                ...b,
                preview: b.preview?.startsWith('blob:') ? null : b.preview
            }));
        }
        return lesson?.contents?.length
            ? lesson.contents.map((c, i) => ({
                id: i + 1,
                type: c.type,
                content: c.content,
                url: c.type === 'video' ? c.content : '',
                file: null,
                preview: c.type === 'image' ? c.content : null,
                original_filename: c.original_filename || null,
            }))
            : [];
    });



    const [errors, setErrors] = useState({
        title: '',
        thumbnail: '',
    });

    const authCtx = useContext(AuthContext);
    const thumbnailInputRef = useRef(null);

    const { cropperModal, handleThumbnailFileSelect } = useCourseThumbnailCrop({
        onCropped: (file) => {
            setThumbnailFile(file);
            setThumbnailPreview(URL.createObjectURL(file));
            setErrors(prev => ({ ...prev, thumbnail: '' }));
        },
        onInvalidFile: (message) => {
            setToast({ open: true, message, severity: 'error' });
        },
    });

    const validate = () => {
        const newErrors = { title: '', thumbnail: '' };
        let isValid = true;
        const toastMessages = [];

        if (!title.trim()) {
            newErrors.title = 'Title is required.';
            isValid = false;
            toastMessages.push('Title is required');
        } else if (title.length > 80) {
            isValid = false;
            toastMessages.push('Title exceeds 80 characters');
        }

        if (blocks.length === 0) {
            isValid = false;
            toastMessages.push('At least one content block required');
        } else {
            // check if blocks are all empty
            const hasValidBlock = blocks.some(b => {
                if (b.type === 'text') return b.content && b.content !== '<p></p>' && b.content.trim() !== '';
                if (b.type === 'video') return !!b.url;
                if (b.type === 'image') return b.file || b.content;
                if (b.type === 'pdf') return b.file || b.content;
                return false;
            });
            if (!hasValidBlock) {
                isValid = false;
                toastMessages.push('At least one valid content block required');
            }
        }

        setErrors(newErrors);

        if (!isValid && toastMessages.length > 0) {
            toastMessages.forEach(msg => {
                setToast({ open: true, message: msg, severity: 'error' });
            });
        }

        return isValid;
    };

    const handleThumbnailUpload = handleThumbnailFileSelect;

    const [status, setStatus] = useState(
        draft?.status ?? (lesson?.status
            ? lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)
            : 'Draft')
    );
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isEditMode) return;
        const draftObj = {
            title,
            thumbnailPreview: thumbnailPreview?.startsWith('blob:') ? null : thumbnailPreview,
            status,
            blocks: blocks.map(b => ({
                ...b,
                file: null,
                preview: b.preview?.startsWith('blob:') ? null : b.preview
            }))
        };
        sessionStorage.setItem(storageKey, JSON.stringify(draftObj));
    }, [title, thumbnailPreview, status, blocks, storageKey, isEditMode]);

    const hasCreatedRef = useRef(false);

const handleSave = async () => {
    const isValid = validate();
    if (!isValid || isloading) return;
    if (!isEditModeLocal && hasCreatedRef.current) {
        setToast({ open: true, message: 'This lesson was already created. Please refresh if you need to edit it.', severity: 'error' });
        return;
    }

    setIsLoading(true);

    try {
        let thumbnailUrl = lesson?.thumbnail_url || null;
        if (thumbnailFile) {
            const imageFormData = new FormData();
            imageFormData.append('image', thumbnailFile);
            const imageResponse = await fetch(
                `${process.env.REACT_APP_API_URI}/v3/admin/assessment/${assessmentId}/updateImage`,
                {
                    method: 'PUT',
                    mode: 'cors',
                    headers: { 'Authorization': 'Bearer ' + authCtx.token },
                    body: imageFormData,
                }
            );
            if (!imageResponse.ok) throw new Error(`Thumbnail upload failed! status: ${imageResponse.status}`);
            const imageData = await imageResponse.json();
            thumbnailUrl = imageData.imagePath;
        }

        const resolvedContents = await Promise.all(
            blocks.map(async (block) => {
                if (block.type === 'text') {
                    if (block.content && block.content !== '<p></p>' && block.content.trim() !== '') {
                        return { type: 'text', content: block.content };
                    }
                    return null;
                }

                if (block.type === 'video') {
                    if (block.url) {
                        const embedUrl = getVideoEmbedUrl(block.url);
                        if (embedUrl) return { type: 'video', content: embedUrl };
                    }
                    return null;
                }

                if (block.type === 'image') {
                    if (block.file) {
                        const formData = new FormData();
                        formData.append('image', block.file);
                        const res = await fetch(
                            `${process.env.REACT_APP_API_URI}/v3/admin/assessment/${assessmentId}/updateImage`,
                            {
                                method: 'PUT',
                                mode: 'cors',
                                headers: { 'Authorization': 'Bearer ' + authCtx.token },
                                body: formData,
                            }
                        );
                        if (!res.ok) throw new Error(`Image upload failed! status: ${res.status}`);
                        const data = await res.json();
                        return { type: 'image', content: data.imagePath };
                    }
                    if (block.content) return { type: 'image', content: block.content };
                }

                if (block.type === 'pdf') {
                    if (block.file) {
                        const formData = new FormData();
                        formData.append('image', block.file);
                        const res = await fetch(
                            `${process.env.REACT_APP_API_URI}/v3/admin/assessment/${assessmentId}/updateImage`,
                            {
                                method: 'PUT',
                                mode: 'cors',
                                headers: { 'Authorization': 'Bearer ' + authCtx.token },
                                body: formData,
                            }
                        );
                        if (!res.ok) throw new Error(`PDF upload failed! status: ${res.status}`);
                        const data = await res.json();
                        return { type: 'pdf', content: data.imagePath, original_filename: block.original_filename || block.file.name };
                    }
                    if (block.content) return { type: 'pdf', content: block.content, original_filename: block.original_filename || block.content.split('/').pop() };
                }

                return null;
            })
        );

        const contents = resolvedContents.filter(Boolean);
        const payload = {
            title,
            thumbnail_url: thumbnailUrl,
            status: status.toLowerCase(),
            contents,
        };

        const url = isEditModeLocal
            ? `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${courseId}/modules/${moduleId}/lessons/${activeLessonId}`
            : `${process.env.REACT_APP_API_URI}/v8/admin/assessment/${assessmentId}/courses/${courseId}/modules/${moduleId}/lessons`;

        const response = await fetch(url, {
            method: isEditModeLocal ? 'PUT' : 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + authCtx.token,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();

        if (data.statusText === 'Success' || response.ok) {
            let newLessonId = activeLessonId;

            if (!isEditModeLocal) {
                // Defensive extraction across likely response shapes
                newLessonId =
                    data?.data?.lessonId  ??
                    data?.data?._id ??
                    data?.data?.lesson?.id ??
                    data?.lesson?.id ??
                    data?.id ??
                    data?._id ??
                    null;
                hasCreatedRef.current = true;

                if (newLessonId) {
                    setCreatedLessonId(newLessonId);
                } else {
                    console.error('Lesson created but no id found in response', data);
                    setToast({
                        open: true,
                        message: 'Lesson saved, but could not switch to edit mode. Please refresh before making further changes.',
                        severity: 'error',
                    });
                }
            }

            sessionStorage.removeItem(storageKey);

            if (onLessonSaved) {
                onLessonSaved({ id: newLessonId, title, thumbnail_url: thumbnailUrl, status: status.toLowerCase(), contents });
            }

            if (newLessonId || isEditModeLocal) {
                setToast({ open: true, message: 'Lesson saved', severity: 'success' });
            }
        } else {
            setToast({ open: true, message: `Failed to save lesson - ${data.message || 'Unknown error'}`, severity: 'error' });
        }

    } catch (err) {
        console.error('Failed to save lesson...', err);
        setToast({ open: true, message: `Failed to save lesson - ${err.message}`, severity: 'error' });
    } finally {
        setIsLoading(false);
    }
};

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className='create-lesson-main-container'>
            <div className='create-course-title'>
                {isEditMode ? "Edit Lesson" : "Let's create your Lesson"}
            </div>
            <div className='create-lesson-detail-container'>
                <div className='lesson-details-main-container'>

                    <div className='status-container'>
                        <div className='status'>Status</div>
                        <div className='draft-dropdown-wrapper' ref={dropdownRef}>
                            <div
                                className='draf-dropdown-contaner'
                                onClick={() => setDropdownOpen(prev => !prev)}
                            >
                                {status}
                                <img
                                    src={draftIcon}
                                    alt="draftIcon"
                                    style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                                />
                            </div>

                            {dropdownOpen && (
                                <div className='draft-dropdown-menu'>
                                    {STATUS_OPTIONS.map((option) => (
                                        <div
                                            key={option}
                                            className={`draft-dropdown-item ${status === option ? 'selected' : ''}`}
                                            onClick={() => {
                                                setStatus(option);
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            <span>{option}</span>
                                            <div className={`draft-dropdown-check ${status === option ? 'active' : ''}`}>
                                                {status === option && <img src={checkIocn} alt='checkIocn' />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='fields-main-container'>
                        <div className='field-title'>Title</div>
                        <div className='field-wrapper'>
                            <input
                                type="text"
                                name="title"
                                maxLength={80}
                                className={`field-input-container title ${(errors.title || title.length > 80) ? 'input-error' : ''}`}
                                placeholder='Type in a Lesson Name'
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                    if (errors.title) setErrors(prev => ({ ...prev, title: '' }));
                                }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'flex-end', marginTop: '4px' }}>
                                <div style={{ fontSize: '12px', color: title.length > 80 ? '#d32f2f' : '#888', marginLeft: '8px' }}>
                                    {title.length}/80
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='thumbnail-container'>
                        <div className='thumbnail-wrapper'>
                            <div className='thumbnail-upload-container'>
                                <div className='title'>Thumbnail</div>
                                <div className={`thumbnail-upload-box ${errors.thumbnail ? 'input-error' : ''}`}>
                                    {thumbnailPreview ? (
                                        <img src={thumbnailPreview} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                                    ) : (
                                        <img src={camera} alt="camera" />
                                    )}
                                </div>

                                {thumbnailPreview ?
                                    <div className='thumbnail-preview-cta-container'>
                                        <div className='replace-thumbnail' onClick={() => thumbnailInputRef.current?.click()}>
                                            <img src={replaceThumbnail} alt="replaceThumbnail" />
                                            Replace Thumbnail
                                        </div>
                                        <button
                                            className='remove-thumbnail'
                                            onClick={() => {
                                                setThumbnailFile(null);
                                                setThumbnailPreview(null);
                                                if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </div> :
                                    <div className='thumbnail-upload-cta-container' onClick={() => thumbnailInputRef.current?.click()}>
                                        <img src={uploadCta} alt="uploadCta" />
                                        Upload Thumbnail
                                    </div>
                                }

                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif,.webp"
                                    ref={thumbnailInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleThumbnailUpload}
                                />
                            </div>
                            <div className='recommended-format'>
                                Cropped to 16:9 (1280×720) to match course display. JPG, PNG, GIF, WebP.
                            </div>
                        </div>
                    </div>

                </div>
                <LessonContent
                    key={activeLessonId || 'new'}
                    blocks={blocks}
                    setBlocks={setBlocks}
                    onSave={handleSave}
                    isloading={isloading}
                    isEditMode={isEditModeLocal}
                    setToast={setToast}
                />
            </div>
            {toasts.map((t, index) => (
                <Snackbar
                    key={t.id}
                    open={true}
                    onClose={handleCloseToast(t.id)}
                    anchorOrigin={{ vertical: 'top', horizontal: t.severity === 'success' ? 'center' : 'right' }}
                    style={t.severity === 'success' ? {} : { top: 24 + index * 65 }}
                >
                    <Alert onClose={handleCloseToast(t.id)} severity={t.severity} sx={{ width: '100%' }}>
                        {t.message}
                    </Alert>
                </Snackbar>
            ))}
            {cropperModal}
        </div>
    )
}

export default CreateLesson